/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { useSelector, shallowEqual } from "react-redux";
import { checklistsInReviews } from "./ReviewsHelper";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import HistoryTab from "./HistoryTab";
import { convertDate } from "@Services/luxonHelper";
import { Tabs, Tab } from "@mui/material";
import { RootState } from "@Reducers/index";
import type { UserDetails } from "@ServerTypes/accounts/accounts.schema";
import type { ChecklistsInReviewsRequest } from "@ServerTypes/reviews/reviews.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { ChecklistChoice, ChecklistHistoryEntry } from "./HistoryTab";

/**
 * One comment on a checklist question. SERVER GAP: `checklistsInReviewsResponse.comments` is
 * `z.array(z.unknown())` and `checklistsInReviews` is `}: any)` server-side, so there is no server type
 * to flow. `createdAt` is read as a fallback for `createdDate` in the sort below.
 */
export interface ChecklistComment {
  id?: string;
  text?: string;
  userId?: string;
  createdDate?: number;
  createdAt?: number;
  official?: boolean;
}

/** The comment-mutation fields of `checklistsInReviewsRequest` that `post()` layers on top. */
type ChecklistCommentPayload = Partial<
  Pick<ChecklistsInReviewsRequest, "commentAction" | "commentId" | "commentText" | "official">
>;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const asString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);
const asNumber = (value: unknown): number | undefined => (typeof value === "number" ? value : undefined);
/** Returns the SAME array when it qualifies, so a store value keeps its identity for `shallowEqual`. */
const asStringArray = (value: unknown): string[] | undefined =>
  Array.isArray(value) && value.every((entry: unknown) => typeof entry === "string") ? value : undefined;

/**
 * The only check between an untyped comment and the render/sort below. Both sources are declared
 * `unknown` server-side — `checklistsInReviewsResponse.comments` is `z.array(z.unknown())` and the
 * stored `reviewResourceResultSchema.checklistSelections` is `z.record(z.string(), z.unknown())` —
 * so each field is read at its own type instead of the array being asserted into shape.
 */
const toChecklistComments = (value: unknown): ChecklistComment[] =>
  Array.isArray(value)
    ? value.filter(isRecord).map((entry) => ({
        id: asString(entry.id),
        text: asString(entry.text),
        userId: asString(entry.userId),
        createdDate: asNumber(entry.createdDate),
        createdAt: asNumber(entry.createdAt),
        official: typeof entry.official === "boolean" ? entry.official : undefined,
      }))
    : [];

/** The same check for `checklistsInReviewsResponse.history`, declared `z.array(z.unknown())`. */
const toChecklistHistory = (value: unknown): ChecklistHistoryEntry[] =>
  Array.isArray(value)
    ? value.filter(isRecord).map((entry) => ({
        id: asString(entry.id),
        userId: asString(entry.userId),
        createdDate: asNumber(entry.createdDate),
        actionType: asString(entry.actionType),
        displayOldLabels: asString(entry.displayOldLabels),
        displayNewLabels: asString(entry.displayNewLabels),
        oldSelection: asStringArray(entry.oldSelection),
        newSelection: asStringArray(entry.newSelection),
        commentText: asString(entry.commentText),
        oldCommentText: asString(entry.oldCommentText),
        commentOwnerId: asString(entry.commentOwnerId),
      }))
    : [];

const sortDesc = <T extends { createdDate?: number; createdAt?: number }>(list: readonly T[] = []) =>
  [...list].sort((a, b) => (b.createdDate ?? b.createdAt ?? 0) - (a.createdDate ?? a.createdAt ?? 0));

interface CommentModalProps {
  open: boolean;
  onClose: () => void;
  reviewId: ObjectIdString;
  fileVersionId: ObjectIdString;
  levelId: ObjectIdString;
  checklistId: ObjectIdString;
  questionId: ObjectIdString;
  checkboxChoices?: ChecklistChoice[];
  readOnly?: boolean;
  projectId: ObjectIdString;
}

const CommentModal: React.FC<CommentModalProps> = ({
  open,
  onClose,
  reviewId,
  fileVersionId,
  levelId,
  checklistId,
  questionId,
  checkboxChoices = [],
  readOnly = false,
  projectId,
}) => {
  const { t } = useTranslation();
  // Everything selected here is handed back BY REFERENCE off the store so `shallowEqual` stays
  // stable; the untyped payloads are converted in the memos below, keyed on those references.
  const { userDetails, rawComments, rawSelected, rawHistory } = useSelector((s: RootState) => {
    const res = (s.reviewData.resources || []).find((r) => r.fileVersionId === fileVersionId);
    // `checklistSelections` is `z.record(z.string(), z.unknown())`, so nothing below its first key
    // is typed (see the SERVER GAP note on ChecklistSelections) — each level is shape-checked on
    // the way down rather than the head being asserted into the nested shape.
    const lvl = res?.checklistSelections?.[levelId];
    const cl = isRecord(lvl) ? lvl[checklistId] : undefined;
    const qNode = isRecord(cl) ? cl[questionId] : undefined;
    const question = isRecord(qNode) ? qNode : undefined;
    return {
      userDetails: s.userDetails,
      rawComments: question?.comments,
      rawSelected: question?.selected,
      rawHistory: question?.history,
    };
  }, shallowEqual);

  const commentsFromStore = useMemo(() => toChecklistComments(rawComments), [rawComments]);
  const historyFromStore = useMemo(() => toChecklistHistory(rawHistory), [rawHistory]);
  const selectedFromStore = useMemo(() => asStringArray(rawSelected) ?? [], [rawSelected]);

  const [comments, setComments] = useState<ChecklistComment[]>(sortDesc(commentsFromStore));
  const [draft, setDraft] = useState<string>("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState<string>("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("comments");
  const [history, setHistory] = useState<ChecklistHistoryEntry[]>(sortDesc(historyFromStore));

  useEffect(() => {
    const fetchComments = async () => {
      if (!open || !questionId) return;
      const res = await checklistsInReviews({
        reviewId,
        fileVersionId,
        levelId,
        checklistId,
        questionId,
        projectId,
        selected: selectedFromStore,
        fetchData: "comments",
      });

      if (res.data.status && res.data.comments) {
        setComments(sortDesc(toChecklistComments(res.data.comments)));
      }
    };

    if (open) {
      fetchComments();
    }
  }, [open, questionId]);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!open || !questionId || activeTab !== "history") return;
      const res = await checklistsInReviews({
        reviewId,
        fileVersionId,
        levelId,
        checklistId,
        questionId,
        projectId,
        selected: selectedFromStore,
        fetchData: "history",
      });

      if (res.data.status && res.data.history) {
        setHistory(sortDesc(toChecklistHistory(res.data.history)));
      }
    };
    fetchHistory();
  }, [activeTab, open, questionId]);

  useEffect(() => {
    setComments(sortDesc(commentsFromStore));
  }, [commentsFromStore]);

  useEffect(() => {
    setHistory(sortDesc(historyFromStore));
  }, [historyFromStore]);

  useEffect(() => {
    if (!open) return;
    setDraft("");
    setEditingId(null);
    setEditingText("");
    setOpenMenuId(null);
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // A click whose target is not an Element (the document itself) has no menu ancestor either,
      // so it closes the menu — reading `.closest` off it used to throw instead.
      if (!(e.target instanceof Element) || !e.target.closest(".comment-menu-container")) setOpenMenuId(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const post = async (payload: ChecklistCommentPayload) => {
    const fetchData = activeTab === "history" ? "both" : "comments";
    return await checklistsInReviews({
      reviewId,
      fileVersionId,
      levelId,
      checklistId,
      questionId,
      projectId,
      selected: selectedFromStore,
      fetchData,
      ...payload,
    });
  };

  type ChecklistsInReviewsResult = Awaited<ReturnType<typeof checklistsInReviews>>;
  const pickComments = (res: ChecklistsInReviewsResult) =>
    res.data.status && res.data.comments ? toChecklistComments(res.data.comments) : null;
  const pickHistory = (res: ChecklistsInReviewsResult) =>
    res.data.status && res.data.history ? toChecklistHistory(res.data.history) : null;

  const addComment = async () => {
    const text = draft.trim();
    if (!text) return;

    const tempId = `tmp_${Date.now()}`;
    const optimistic = {
      id: tempId,
      text,
      userId: userDetails._id,
      createdDate: Date.now(),
      official: false,
    };

    setComments((prev) => sortDesc([optimistic, ...prev]));
    setDraft("");

    const res = await post({ commentAction: "add", commentText: text });
    const next = pickComments(res);
    const nextHistory = pickHistory(res);

    if (next) setComments(sortDesc(next));
    if (nextHistory) {
      setHistory(sortDesc(nextHistory));
    }
  };

  const beginEdit = (c: ChecklistComment) => {
    setOpenMenuId(null);
    setEditingId(c.id!);
    setEditingText(c.text || "");
  };

  const saveEdit = async () => {
    const text = editingText.trim();
    if (!text || !editingId) return;

    setComments((prev) => sortDesc(prev.map((c) => (c.id === editingId ? { ...c, text } : c))));

    const res = await post({
      commentAction: "edit",
      commentId: editingId,
      commentText: text,
    });
    const next = pickComments(res);
    const nextHistory = pickHistory(res);

    if (next) setComments(sortDesc(next));
    if (nextHistory) {
      setHistory(sortDesc(nextHistory));
    }

    setEditingId(null);
    setEditingText("");
  };

  const deleteComment = async (id: string) => {
    setComments((prev) => prev.filter((c) => c.id !== id));
    setOpenMenuId(null);

    const res = await post({ commentAction: "delete", commentId: id });
    const next = pickComments(res);
    const nextHistory = pickHistory(res);

    if (next) setComments(sortDesc(next));
    if (nextHistory) {
      setHistory(sortDesc(nextHistory));
    }
  };

  const toggleOfficial = async (id: string, next: boolean) => {
    setComments((prev) => prev.map((c) => (c.id === id ? { ...c, official: !!next } : c)));
    setOpenMenuId(null);

    const res = await post({
      commentAction: "toggleOfficial",
      commentId: id,
      official: !!next,
    });
    const nextList = pickComments(res);
    const nextHistory = pickHistory(res);

    if (nextList) setComments(sortDesc(nextList));
    if (nextHistory) {
      setHistory(sortDesc(nextHistory));
    }
  };

  const sortedComments = useMemo(() => sortDesc(comments), [comments]);

  const header = <div className="flex items-center px-6 text-lg font-semibold">{t("Activity")}</div>;

  const tabs = (
    <Tabs
      value={activeTab}
      onChange={(_, newValue) => setActiveTab(newValue)}
      sx={{
        px: 3,
        borderBottom: 1,
        borderColor: "divider",
        minHeight: 0,
        "& .MuiTab-root": {
          fontSize: "0.875rem",
          fontWeight: 500,
          textTransform: "none",
          minHeight: "1.25rem",
          py: "0.5rem",
        },
      }}
    >
      <Tab label={t("Comments")} value="comments" />
      <Tab label={t("History")} value="history" />
    </Tabs>
  );

  const composer = readOnly ? null : (
    <div className="px-6 py-4 border-b border-gray-200">
      <div className="flex items-center gap-3">
        <input
          className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm outline-none"
          placeholder={t("Type Something")}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              addComment();
            }
          }}
        />
        <ButtonComponent disabled={!draft.trim()} buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={addComment}>
          {t("Post")}
        </ButtonComponent>
      </div>
    </div>
  );

  const list = (
    <div className="max-h-[31.25rem] overflow-y-auto">
      <div className="px-6 py-4">
        {sortedComments.map((c) => {
          const isMine = c.userId === userDetails._id;
          // KNOWN BUG, preserved verbatim: `isAdmin` is NOT a field of `userDetailsSchema`
          // (accounts.schema.ts) and this is the only place in web that reads it, so it is always
          // `undefined` — `canDelete` therefore collapses to `isMine` and a portal admin can never
          // delete someone else's checklist comment, which is plainly the intent here. Does the server
          // read it? No — it is a client-side render gate; the server does not authorise the delete by
          // admin either. Fix: derive admin-ness from the real field (e.g. portalRole) and add it to
          // `userDetailsSchema`. Intersection assertion, erased.
          const isAdmin = (userDetails as UserDetails & { isAdmin?: boolean }).isAdmin;
          const canEdit = isMine;
          const canDelete = isMine || isAdmin;

          return (
            <div key={c.id} className="mb-3 overflow-visible">
              <div className="grid grid-cols-[1fr_auto] gap-x-3 items-start">
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-2 min-h-6 mb-1">
                    <UserNameTagComponent id={c.userId} />

                    {c.official && (
                      <ButtonComponent
                        className="rounded-2xl cursor-default pointer-events-none"
                        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                      >
                        {t("Official Response")}
                      </ButtonComponent>
                    )}
                  </div>

                  {editingId === c.id ? (
                    <div>
                      <input
                        className="w-full border border-gray-200 rounded px-3 py-2 text-sm outline-none leading-[1.4]"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            saveEdit();
                          }
                        }}
                      />
                      <div className="flex gap-2 mt-2">
                        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={saveEdit}>
                          {t("Save")}
                        </ButtonComponent>
                        <ButtonComponent
                          buttonType={BUTTON_TYPES.BORDER_BUTTON}
                          onClick={() => {
                            setEditingId(null);
                            setEditingText("");
                          }}
                        >
                          {t("Cancel")}
                        </ButtonComponent>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700 leading-[1.5] whitespace-pre-wrap break-words">{c.text}</div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <div className="text-xs text-gray-500 text-end whitespace-nowrap">
                    {convertDate(Number(c.createdDate), "dateForText")}
                  </div>

                  {!readOnly && (canEdit || canDelete) && (
                    <div className="comment-menu-container relative">
                      <ButtonComponent
                        className="bg-transparent border-none px-2 py-1 text-lg text-gray-500 leading-none"
                        aria-label="Comment actions"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === c.id ? null : c.id!);
                        }}
                      >
                        {"⋮"}
                      </ButtonComponent>

                      {openMenuId === c.id && (
                        <div
                          role="menu"
                          className="absolute end-0 top-full bg-white border border-gray-200 rounded-lg shadow-[0_0.375rem_1.25rem_rgba(31,42,68,0.12),0_0.125rem_0.5rem_rgba(31,42,68,0.08)] z-10 min-w-[13.75rem] mt-1.5"
                          onClick={(e: React.SyntheticEvent) => e.stopPropagation()}
                        >
                          {canEdit && (
                            <ButtonComponent
                              className="w-full text-start flex justify-start px-4 py-3 text-sm bg-transparent text-[#1F2A44] rounded-none hover:bg-[#F5F7FB]"
                              onClick={() => beginEdit(c)}
                            >
                              {t("Edit")}
                            </ButtonComponent>
                          )}

                          {canDelete && (
                            <>
                              <div className="h-px bg-[#E6EDF5]" />
                              <ButtonComponent
                                className="w-full text-start flex justify-start px-4 py-3 text-sm bg-transparent text-[#1F2A44] rounded-none hover:bg-[#F5F7FB]"
                                onClick={() => deleteComment(c.id!)}
                              >
                                {t("Delete")}
                              </ButtonComponent>
                            </>
                          )}

                          <>
                            <div className="h-px bg-[#E6EDF5]" />
                            <ButtonComponent
                              className="w-full text-start flex justify-start px-4 py-3 text-sm bg-transparent text-[#1F2A44] rounded-none hover:bg-[#F5F7FB]"
                              onClick={() => toggleOfficial(c.id!, !c.official)}
                            >
                              {c.official ? t("Unmark Official") : t("Mark as Official")}
                            </ButtonComponent>
                          </>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {sortedComments.length === 0 && (
          <div className="text-center text-gray-400 text-sm py-10">{t("No comments yet.")}</div>
        )}
      </div>
    </div>
  );

  const content = (
    <div>
      {tabs}
      {activeTab === "comments" ? (
        <>
          {!readOnly && composer}
          {list}
        </>
      ) : (
        <HistoryTab history={history} choices={checkboxChoices} />
      )}
    </div>
  );

  return (
    <ModalStateDialogComponent
      isVisible={open}
      allowModalPadding={false}
      width="56.25rem"
      headerTitle={header}
      modelContent={content}
      onCancel={onClose}
    />
  );
};

export default CommentModal;
